import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Form,
  LoadingSpinner,
  ModeToggle,
  PasswordFormField,
  Typography,
} from "@/components/ui";
import { LawWorksLogo } from "@/components/ui/LawWorksLogo/LawWorksLogo";
import { useConfirmSignIn } from "@/lib/data/react-query/auth/auth";
import { PasswordSchema } from "@/lib/data/schemas";
import { redirectIfAuthenticated } from "@/lib/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

export const Route = createFileRoute("/change-password")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    redirectIfAuthenticated({ session: context.session, location });
  },
});

export const changePasswordFormSchema = z
  .object({
    newPassword: PasswordSchema,
    confirmNewPassword: PasswordSchema,
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
    path: ["confirmNewPassword"],
  });

function RouteComponent() {
  const confirmSignIn = useConfirmSignIn();
  const navigate = useNavigate({ from: "/change-password" });

  const form = useForm<z.infer<typeof changePasswordFormSchema>>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      newPassword: "",
    },
    mode: "onSubmit",
  });

  const onSubmit = async () => {
    await confirmSignIn
      .mutateAsync({
        challengeResponse: form.getValues("newPassword"),
      })
      .then((res) => {
        switch (res.signInStep) {
          case "DONE": {
            toast.success("Password changed successfully");
            void navigate({ to: "/" });
            window.location.reload();
            break;
          }
          case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP": {
            toast.success(
              "Password changed successfully! Now set up two-factor authentication.",
            );
            // Store only the necessary TOTP data (not the full object)
            // Use the stored email for personalization
            const storedEmail = localStorage.getItem("user-email") || "User";
            const uri = res.totpSetupDetails.getSetupUri(
              "LawWorks",
              storedEmail,
            );
            const sharedSecret = res.totpSetupDetails.sharedSecret;

            localStorage.setItem(
              "mfa-setup-required",
              "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
            );
            localStorage.setItem("totp-setup-uri", uri.toString());
            localStorage.setItem("totp-shared-secret", sharedSecret);
            void navigate({ to: "/login" });
            break;
          }
          case "CONFIRM_SIGN_IN_WITH_TOTP_CODE": {
            toast.success(
              "Password changed successfully! Please verify with your authenticator app.",
            );
            localStorage.setItem(
              "mfa-setup-required",
              "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
            );
            void navigate({ to: "/login" });
            break;
          }
          default: {
            toast.error(
              `You have an unhandled auth step (${res.signInStep}), please contact your administrator`,
            );
            break;
          }
        }
      })
      .catch((err: unknown) => {
        if (err instanceof Error) {
          if (err.name === "InvalidPasswordException") {
            form.setError("newPassword", {
              message:
                "Password must contain: \n- 8 characters \n- 1 uppercase \n- 1 lowercase, \n- 1 number, \n - 1 special character",
            });
          }
          if (err.name === "SignInException") {
            toast.error(
              "A password reset is not required. Please login with your current password",
            );
            void navigate({ to: "/login" });
          }
        }
      });
  };

  return (
    <div className="flex h-[99vh] w-[99vw] grow content-center justify-center overflow-hidden">
      <div className="sm:px-auto my-auto w-full max-w-[500px] px-4">
        <Card className="p-6">
          <CardHeader>
            <LawWorksLogo className="justify-self-center" />
          </CardHeader>
          <CardContent>
            <Typography>Your account requires a password change.</Typography>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="mt-8 flex flex-col gap-y-4"
              >
                <PasswordFormField
                  register={form.register("newPassword")}
                  label="New password"
                />
                <PasswordFormField
                  register={form.register("confirmNewPassword")}
                  label="Confirm new password"
                />
                <Button
                  type="submit"
                  variant="foreground"
                  className="mt-4 w-full"
                >
                  {confirmSignIn.isPending ? (
                    <LoadingSpinner />
                  ) : (
                    "Change password"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <div className="float-right pr-6">
            <ModeToggle />
          </div>
          <CardFooter className="flex flex-col items-start">
            <span className="mt-2 flex flex-wrap text-sm font-light">
              <Link to="/login">
                <Typography variant="link">Back to login</Typography>
              </Link>
            </span>
          </CardFooter>
        </Card>
      </div>
      <div></div>
    </div>
  );
}
